/**
 * quoteProposal.ts
 * Phase 5, Items 7 + 8 — Frontend service for the Generate Proposal endpoint
 * and the ProposalDocument list endpoint.
 *
 * POST /api/quotes/:quoteKey/generate-proposal
 *   Receives a .docx blob and triggers a browser file download.
 *
 * GET /api/quotes/:quoteKey/proposals
 *   Returns the list of ProposalDocument metadata rows (latest first).
 *   Used for the "Last generated …" display in the quote editor.
 */

import apiClient from './apiClient';
import type { ProposalDocument } from '../types/schedule';

/**
 * Generates a proposal .docx for the given quote and downloads it to the browser.
 *
 * @param quoteKey    — the UUID of the quote
 * @param quoteNumber — used to derive the download filename (Proposal-{quoteNumber}-vN.docx)
 */
export async function generateProposal(
  quoteKey: string,
  quoteNumber: string,
): Promise<void> {
  const response = await apiClient.post(
    `/quotes/${quoteKey}/generate-proposal`,
    {},
    { responseType: 'blob' },
  );

  // Extract the server-provided filename from Content-Disposition if available,
  // otherwise fall back to a sensible default.
  const disposition = response.headers['content-disposition'] as string | undefined;
  let filename = `Proposal-${quoteNumber}.docx`;
  if (disposition) {
    const match = /filename="([^"]+)"/.exec(disposition);
    if (match) {
      filename = match[1];
    }
  }

  const url = window.URL.createObjectURL(
    new Blob([response.data as BlobPart], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

/**
 * Returns the list of ProposalDocument rows for the given quote, ordered latest first.
 * Used to display "Last generated: {date} by {email} (v{N})" in the quote editor.
 *
 * @param quoteKey — the UUID of the quote
 */
export async function listProposalDocuments(quoteKey: string): Promise<ProposalDocument[]> {
  const response = await apiClient.get<ProposalDocument[]>(
    `/quotes/${quoteKey}/proposals`,
  );
  return response.data;
}
